import { useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import { useAuth } from '../auth/AuthContext'
import { api } from '../api'
import { initials, plural } from '../lib/format'
import { screenOf, recordOf, panelFor, recall, remember } from '../lib/iconic-view'
import type { Remembered } from '../lib/iconic-view'
import { MenuIcon, SignOutIcon, SparkleIcon } from './icons'
import { IconicPanel } from './IconicPanel'

/** A phone: the same width at which the Iconic panel covers the page (FR-67). */
const PHONE = '(max-width: 760px)'

/** Whether the window is phone width now, kept up to date as it is resized or turned. */
function usePhone() {
  const query = () => (typeof window !== 'undefined' && typeof window.matchMedia === 'function' ? window.matchMedia(PHONE) : null)
  const [phone, setPhone] = useState(() => query()?.matches ?? false)
  useEffect(() => {
    const q = query()
    if (!q) return
    const on = () => setPhone(q.matches)
    on()
    q.addEventListener('change', on)
    return () => q.removeEventListener('change', on)
  }, [])
  return phone
}

export function Shell({ children }: { children: ReactNode }) {
  const { me, signOut, isAdmin, isSuperAdmin } = useAuth()
  const [navOpen, setNavOpen] = useState(true)
  const location = useLocation()

  /*
   * On a phone the side menu is folded away behind a button in the top bar,
   * and the page has the whole width (FR-67, STE-1070). Opened, it lies over
   * the page; it closes from its own button, from the menu button, with
   * Escape, by a tap on the page beside it, and when a screen is chosen.
   * Wider than a phone none of this is drawn and the menu is as it always was.
   */
  const phone = usePhone()
  const [menuOpen, setMenuOpen] = useState(false)
  const menuButton = useRef<HTMLButtonElement>(null)
  const menu = useRef<HTMLElement>(null)
  const closeMenu = (refocus: boolean) => {
    setMenuOpen(false)
    if (refocus) menuButton.current?.focus()
  }
  useEffect(() => { if (!phone) setMenuOpen(false) }, [phone])
  // Any move to another screen closes it, the back button too.
  useEffect(() => { setMenuOpen(false) }, [location.pathname])
  useEffect(() => {
    if (!menuOpen) return
    menu.current?.querySelector<HTMLElement>('a, button')?.focus()
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') closeMenu(true) }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [menuOpen]) // eslint-disable-line react-hooks/exhaustive-deps

  /*
   * Iconic (STE-997). Asked for once: a company it is switched off for gets
   * 404 from every Iconic route, and then nothing of it is drawn.
   */
  const [iconicOn, setIconicOn] = useState(false)
  const [rem, setRemState] = useState<Remembered>(() => recall(me?.userId ?? ''))
  const opener = useRef<HTMLButtonElement>(null)
  /*
   * Always applied to the latest value, never to the one a slow call started
   * with, so a change made while Iconic was starting a chat is not undone
   * when the chat arrives.
   */
  const userId = me?.userId
  const setRem = (next: Remembered | ((r: Remembered) => Remembered)) => {
    setRemState((prev) => {
      const r = typeof next === 'function' ? next(prev) : next
      if (userId) remember(userId, r)
      return r
    })
  }
  useEffect(() => {
    if (!me || me.role === 'superadmin') { setIconicOn(false); return }
    setRemState(recall(me.userId))
    let live = true
    api.iconicChats().then(() => { if (live) setIconicOn(true) }, () => { if (live) setIconicOn(false) })
    return () => { live = false }
  }, [me?.userId, me?.role]) // eslint-disable-line react-hooks/exhaustive-deps

  if (!me) return null

  const pending = me.pendingApprovals
  const roleLabel =
    me.role === 'superadmin' ? 'Stello operations'
      : me.role === 'admin' ? 'HR admin'
        : 'People manager'
  const onJds = location.pathname.startsWith('/job-descriptions')
  const onMarket = location.pathname.startsWith('/market-data')
  const onBenchmarking = location.pathname.startsWith('/benchmarking')
  const onMatchAJob = location.pathname.startsWith('/match-a-job')
  const onSalaryRanges = location.pathname.startsWith('/salary-ranges')
  const onCompanies = location.pathname.startsWith('/companies')
  const screen = screenOf(location.pathname)
  const iconicHere = iconicOn && !isSuperAdmin && panelFor(me.role, screen)
  const panelOpen = iconicHere && rem.open

  return (
    <div className="app">
      <a className="skip-link" href="#main">Skip to main content</a>

      <header className="topbar">
        <div className="topbar__left">
          {phone && (
            <button
              ref={menuButton}
              type="button"
              className="nav-open"
              aria-label="Menu"
              aria-expanded={menuOpen}
              aria-controls="workspace-menu"
              onClick={() => (menuOpen ? closeMenu(false) : setMenuOpen(true))}
            >
              <MenuIcon />
            </button>
          )}
          <span className="mark" aria-hidden="true">✓</span>
          <span className="topbar__name">Stello</span>
          <span className="topbar__rule" aria-hidden="true" />
          <span className="topbar__module">
            {isSuperAdmin ? 'Setting up companies' : 'Job Descriptions and Compensation'}
          </span>
        </div>
        <div className="topbar__right">
          {iconicHere && (
            <button ref={opener} type="button" aria-label="Iconic" className={`iconic-open${panelOpen && rem.size !== 'min' ? ' iconic-open--on' : ''}`}
              aria-expanded={panelOpen && rem.size !== 'min'}
              onClick={() => setRem(panelOpen && rem.size !== 'min' ? { ...rem, open: false } : { ...rem, open: true, size: rem.size === 'min' ? rem.wasSize : rem.size })}>
              <SparkleIcon size={12} />
              <span>Iconic</span>
            </button>
          )}
          <div className="user-chip">
            <span className="avatar" aria-hidden="true">{initials(me.name)}</span>
            <span className="user-chip__text">
              <b>{me.name}</b>
              <span>{roleLabel}</span>
            </span>
          </div>
        </div>
      </header>

      <div className={`body${panelOpen && rem.size === 'max' ? ' body--iconic-max' : ''}`}>
        {phone && menuOpen && <div className="nav-scrim" aria-hidden="true" onClick={() => closeMenu(true)} />}
        {phone && !menuOpen ? null : (phone || navOpen) ? (
          <nav
            ref={menu}
            id={phone ? 'workspace-menu' : undefined}
            className={phone ? 'nav nav--phone' : 'nav'}
            aria-label="Workspace"
            onClick={phone ? (e) => { if ((e.target as Element).closest('a')) closeMenu(false) } : undefined}
          >
            <div className="nav__head">
              <span>Workspace</span>
              <button
                type="button"
                className="nav__toggle"
                onClick={() => (phone ? closeMenu(true) : setNavOpen(false))}
                aria-label={phone ? 'Close the menu' : 'Collapse workspace navigation'}
              >
                «
              </button>
            </div>

            {/*
              A super admin has no library and no market data. Rather than
              show them two links that open nothing, their navigation is the
              one thing they can actually do.
            */}
            {isSuperAdmin ? (
              <NavLink
                to="/companies"
                className="nav__item"
                aria-current={onCompanies ? 'page' : undefined}
              >
                <span>Companies</span>
              </NavLink>
            ) : (
              <NavLink
                to="/job-descriptions"
                className="nav__item"
                aria-current={onJds ? 'page' : undefined}
              >
                <span>Job Descriptions</span>
                {pending > 0 && (
                  <span className="badge">
                    {pending}
                    <span className="sr-only"> {plural(pending, 'approval')} waiting</span>
                  </span>
                )}
              </NavLink>
            )}

            {/*
              Market data is absent for a manager, not greyed out and not shown
              with a lock on it. Survey data is licensed under terms about who
              may see it, and a manager who has never been told the module
              exists should not learn about it from the navigation. The routes
              refuse them too, which is the control that actually matters.
            */}
            {isAdmin && (
              <NavLink
                to="/market-data"
                className="nav__item"
                aria-current={onMarket ? 'page' : undefined}
              >
                <span>Market Data</span>
              </NavLink>
            )}

            {/*
              Below Market Data, because a benchmark is made from it and the
              order on screen should read the way the work does.

              Shown to an admin who has no survey yet, deliberately. The screen
              then explains that benchmarking needs market data first and
              points at adding one. Hiding it would leave them wondering what
              the product does. A manager never sees it, and the routes behind
              it refuse their token.
            */}
            {isAdmin && (
              <NavLink
                to="/benchmarking"
                className="nav__item"
                aria-current={onBenchmarking ? 'page' : undefined}
              >
                <span>Benchmarking</span>
              </NavLink>
            )}

            {/*
              Below Benchmarking, because it answers the same question for a
              role that does not exist yet, and somebody reads down to it after
              finding Benchmarking only works on roles they have saved.

              Shown to an admin with no survey, like Benchmarking, and for the
              same reason: the screen then explains that it needs market data
              first. A manager never sees it, and the routes refuse them too.
            */}
            {isAdmin && (
              <NavLink
                to="/match-a-job"
                className="nav__item"
                aria-current={onMatchAJob ? 'page' : undefined}
              >
                <span>Match a job</span>
              </NavLink>
            )}

            {/*
              Last, because a range is made from a benchmark and the order on
              screen should read the way the work does: roles, then market
              data, then benchmarking, then what a benchmark becomes.
            */}
            {isAdmin && (
              <NavLink
                to="/salary-ranges"
                className="nav__item"
                aria-current={onSalaryRanges ? 'page' : undefined}
              >
                <span>Salary Ranges</span>
              </NavLink>
            )}

            <div className="nav__foot">
              <div className="nav__company">
                {me.tenant.name}
                <br />
                {isSuperAdmin ? 'Setting up companies' : plural(me.jdCount, 'job description')}
              </div>
              {/* Icon only, but it still carries a name for screen readers. */}
              <button
                type="button"
                className="icon-btn icon-btn--signout"
                onClick={() => void signOut()}
                aria-label="Sign out"
                title="Sign out"
              >
                <SignOutIcon />
              </button>
            </div>
          </nav>
        ) : (
          <nav className="nav nav--rail" aria-label="Workspace">
            <button
              type="button"
              className="nav__toggle nav__toggle--rail"
              onClick={() => setNavOpen(true)}
              aria-label="Expand workspace navigation"
            >
              »
            </button>

            {isSuperAdmin ? (
              <NavLink
                to="/companies"
                className="nav__item nav__item--rail"
                aria-label="Companies"
                aria-current={onCompanies ? 'page' : undefined}
              >
                C
              </NavLink>
            ) : (
              <NavLink
                to="/job-descriptions"
                className="nav__item nav__item--rail"
                aria-label={`Job Descriptions${pending ? `, ${plural(pending, 'approval')} waiting` : ''}`}
                aria-current={onJds ? 'page' : undefined}
              >
                J
                {pending > 0 && <span className="badge badge--dot" aria-hidden="true">{pending}</span>}
              </NavLink>
            )}

            <button
              type="button"
              className="icon-btn icon-btn--signout icon-btn--rail"
              onClick={() => void signOut()}
              aria-label="Sign out"
              title="Sign out"
            >
              <SignOutIcon />
            </button>
          </nav>
        )}

        <main className="main" id="main" tabIndex={-1}>
          <div className="main__inner">{children}</div>
        </main>

        {panelOpen && (
          <IconicPanel me={me} screen={screen} record={recordOf(location.pathname)} rem={rem} setRem={setRem} opener={opener} />
        )}
      </div>
    </div>
  )
}
